"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormatService = exports.NO_CURRENCY_KEY = exports.NBSP = exports.SEPARATOR_COMA = exports.SEPARATOR_DOT = void 0;
const wnumb_1 = __importDefault(require("wnumb"));
const importerModulePublication_1 = require("../publication/importer/importerModulePublication");
const common_1 = require("../utils/common");
exports.SEPARATOR_DOT = '.';
exports.SEPARATOR_COMA = ',';
exports.NBSP = '\u00A0';
exports.NO_CURRENCY_KEY = 'no_currency';
const TIME_UNIT_CODE_HRS = 'Hrs';
const TIME_UNIT_CODE_MIN = 'Min';
class FormatService {
    constructor(publicationProvider) {
        this.format = (value, templateKey) => {
            const template = this.getTemplates(templateKey);
            const roundedValue = this.roundByRoundingDecimalSetting(value, template.options);
            return template.template.to(roundedValue);
        };
        this.getFormatOptions = (templateKey) => this.getTemplates(templateKey).options;
        this.formatWithOptions = (value, options) => {
            const roundedByRoundingDecimal = this.roundByRoundingDecimalSetting(value, options);
            const roundedByDecimalPlaces = this.roundByDecimalPlacesSetting(roundedByRoundingDecimal, options);
            const useZeroDecimalPlaces = options.removeZeros && roundedByDecimalPlaces % 1 === 0;
            return useZeroDecimalPlaces
                ? (0, wnumb_1.default)(Object.assign(Object.assign({}, options), { decimals: 0 })).to(roundedByRoundingDecimal)
                : (0, wnumb_1.default)(Object.assign({}, options)).to(roundedByRoundingDecimal);
        };
        this.formatNoUnit = (value, templateKey) => {
            const template = this.getTemplates(templateKey);
            const roundedValue = this.roundByRoundingDecimalSetting(value, template.options);
            return template.noUnitTemplate.to(roundedValue);
        };
        this.getUnitInfo = (templateKey) => {
            var _a;
            const valueSetting = this.getTemplates(templateKey).valueSetting;
            return ({
                unit: (_a = valueSetting.unit) === null || _a === void 0 ? void 0 : _a.value,
                placement: valueSetting.unitSymbolPlacementKind === importerModulePublication_1.UnitSymbolPlacementKind.beforeValue ? 'before' : 'after'
            });
        };
        this.formatNumber = (value) => {
            const template = this.getNumberTemplate();
            const roundedValue = this.roundByRoundingDecimalSetting(value, template.options);
            return template.template.to(roundedValue);
        };
        this.getFormatNumberOptions = () => this.getNumberTemplate().options;
        this.formatPrice = (value, currencySymbol) => {
            const template = this.getPriceTemplate(currencySymbol);
            const rounded = this.roundByDecimalPlacesSetting(value, template.options);
            const useNoDecimalsTemplate = template.options.removeZeros && rounded % 1 === 0;
            const useTemplate = useNoDecimalsTemplate
                ? template.templateNoDecimals
                : template.template;
            return useTemplate.to(value);
        };
        this.getFormatPriceOptions = (currencySymbol) => this.getPriceTemplate(currencySymbol).options;
        this.formatChargingTime = (time) => {
            const chargingTimeFormat = this.publicationProvider.publication.importer.chargingTimeFormat;
            return chargingTimeFormat && chargingTimeFormat.code === '1'
                ? this.formatTimeSpan(time, (hours, minutes) => hours + ':' + (((minutes > 10 ? '' : '0') + minutes)))
                : this.format(time, 'K2Tools.TimeUnit');
        };
        this.formatTimeSpan = (timeSpan, format) => {
            const totalSeconds = this.toSeconds(timeSpan);
            const hoursSegment = Math.floor(totalSeconds / 3600);
            const minutesSegment = Math.floor((totalSeconds - (hoursSegment * 3600)) / 60);
            return format(hoursSegment, minutesSegment);
        };
        this.getTimeUnitCode = () => this.timeUnitCode && this.timeUnitCode.length
            ? this.timeUnitCode
            : this.timeUnitCode = this.getValueSetting('K2Tools.TimeUnit').unit.code;
        this.toSeconds = (time) => {
            const timeUnitCode = this.getTimeUnitCode();
            return timeUnitCode === TIME_UNIT_CODE_HRS ? time * 3600 :
                timeUnitCode === TIME_UNIT_CODE_MIN ? time * 60 :
                    time;
        };
        this.getNumberTemplate = () => this.numberTemplate ? this.numberTemplate : this.resolveNumberTemplate();
        this.resolveNumberTemplate = () => {
            const importer = this.publicationProvider.publication.importer;
            const templateOptions = {
                decimals: importer.numberDigitsAfterDecimal,
                mark: FormatService.resolveGroupingSymbol(importer.numberGroupingSymbol),
                thousand: importer.numberUseThousandSeparator
                    ? FormatService.resolveThousandSeparator(importer.numberThousandSeparator) : undefined,
                removeZeros: false,
                roundingDecimals: importer.numberRoudingDecimal
            };
            this.numberTemplate = {
                template: (0, wnumb_1.default)(templateOptions),
                options: templateOptions
            };
            return this.numberTemplate;
        };
        this.getPriceTemplate = (currencySymbol) => {
            var _a, _b;
            const forCurrency = currencySymbol && currencySymbol.length
                ? currencySymbol
                : (_b = (_a = this.publicationProvider.publication.importer.currency) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : exports.NO_CURRENCY_KEY;
            return this.priceTemplates[forCurrency]
                ? this.priceTemplates[forCurrency]
                : this.resolvePriceTemplate(forCurrency);
        };
        this.resolvePriceTemplate = (forCurrency) => {
            var _a, _b, _c, _d, _e, _f, _g;
            const importer = this.publicationProvider.publication.importer;
            const tool = this.publicationProvider.tool;
            const symbol = forCurrency !== exports.NO_CURRENCY_KEY ? forCurrency : undefined;
            const decimals = (_b = (_a = tool.currencyDigitsAfterDecimal) !== null && _a !== void 0 ? _a : importer.currencyDigitsAfterDecimal) !== null && _b !== void 0 ? _b : undefined;
            const currencyGroupingSymbol = (_c = tool.currencyGroupingSymbol) !== null && _c !== void 0 ? _c : importer.currencyGroupingSymbol;
            const mark = FormatService.resolveCurrencyGroupingSymbol(currencyGroupingSymbol);
            const currencyUseThousandSeparator = (_d = tool.currencyUseThousandSeparator) !== null && _d !== void 0 ? _d : importer.currencyUseThousandSeparator;
            const currencyThousandSeparator = (_e = tool.currencyThousandSeparator) !== null && _e !== void 0 ? _e : importer.currencyThousandSeparator;
            const thousand = currencyUseThousandSeparator
                ? FormatService.resolveCurrencyThousandSeparator(currencyThousandSeparator)
                : undefined;
            const currencySymbolPlacement = (_f = tool.currencySymbolPlacement) !== null && _f !== void 0 ? _f : importer.currencySymbolPlacement;
            const { prefix, suffix } = FormatService.resolveCurrencyPrefixSufix(currencySymbolPlacement, forCurrency);
            const removeZeros = (_g = tool.currencyRemoveZerosAfterDecimalPoint) !== null && _g !== void 0 ? _g : importer.currencyRemoveZerosAfterDecimalPoint;
            const roundingDecimals = null;
            const templateOptions = { symbol, decimals, mark, thousand, prefix, suffix, removeZeros, roundingDecimals };
            this.priceTemplates[forCurrency] = {
                template: (0, wnumb_1.default)(templateOptions),
                templateNoDecimals: (0, wnumb_1.default)(Object.assign(Object.assign({}, templateOptions), { decimals: 0 })),
                options: templateOptions
            };
            return this.priceTemplates[forCurrency];
        };
        this.getTemplates = (templateKey) => this.templates[templateKey] ? this.templates[templateKey] : this.resolveTemplate(templateKey);
        this.resolveTemplate = (templateKey) => {
            const valueSetting = this.getValueSetting(templateKey);
            const templateOptions = this.getTemplateOptions(valueSetting);
            const noUnitTemplateOptions = this.getTemplateOptions(valueSetting, false);
            const templates = {
                template: (0, wnumb_1.default)(templateOptions),
                noUnitTemplate: (0, wnumb_1.default)(noUnitTemplateOptions),
                valueSetting: valueSetting,
                options: templateOptions
            };
            this.templates[templateKey] = templates;
            return templates;
        };
        this.getValueSetting = (templateKey) => this.getValueSettingForTool(templateKey) || this.getValueSettingForImporter(templateKey);
        this.getValueSettingForTool = (templateKey) => { var _a; return (_a = this.publicationProvider.tool.valueSettings) === null || _a === void 0 ? void 0 : _a.find(setting => setting.unit && setting.code === templateKey); };
        this.getValueSettingForImporter = (templateKey) => this.publicationProvider.publication.importer.valueSettings.find(setting => setting.code === templateKey);
        this.getTemplateOptions = (valueSettings, withUnit = true) => (Object.assign(Object.assign({ symbol: valueSettings.unit.value, unitCode: valueSettings.unit.code, decimals: valueSettings.numberDigitsAfterDecimal || 0, mark: FormatService.resolveGroupingSymbol(valueSettings.numberGroupingSymbolKind), thousand: FormatService.resolveThousandSeparator(valueSettings.numberThousandSeparatorKind) }, (withUnit && FormatService.resolvePrefixSufix(valueSettings.unitSymbolPlacementKind, valueSettings.unit))), { removeZeros: false, roundingDecimals: valueSettings.numberRoudingDecimal }));
        this.roundByRoundingDecimalSetting = (value, options) => (0, common_1.notNullOrUndefined)(options.roundingDecimals)
            ? +value.toFixed(options.roundingDecimals)
            : value;
        this.roundByDecimalPlacesSetting = (value, options) => (0, common_1.notNullOrUndefined)(options.decimals)
            ? +value.toFixed(options.decimals)
            : value;
        this.timeUnitCode = '';
        this.numberTemplate = undefined;
        this.templates = {};
        this.priceTemplates = {};
        this.publicationProvider = publicationProvider;
    }
}
exports.FormatService = FormatService;
FormatService.resolveGroupingSymbol = (kind) => kind === importerModulePublication_1.NumberGroupingSymbolKind.dot ? exports.SEPARATOR_DOT :
    kind === importerModulePublication_1.NumberGroupingSymbolKind.comma ? exports.SEPARATOR_COMA :
        undefined;
FormatService.resolveCurrencyGroupingSymbol = (kind) => kind === importerModulePublication_1.CurrencyGroupingSymbolKind.dot ? exports.SEPARATOR_DOT :
    kind === importerModulePublication_1.CurrencyGroupingSymbolKind.comma ? exports.SEPARATOR_COMA :
        undefined;
FormatService.resolveThousandSeparator = (value) => value === importerModulePublication_1.NumberThousandSeparatorKind.dot ? exports.SEPARATOR_DOT :
    value === importerModulePublication_1.NumberThousandSeparatorKind.comma ? exports.SEPARATOR_COMA :
        value === importerModulePublication_1.NumberThousandSeparatorKind.space ? exports.NBSP :
            undefined;
FormatService.resolveCurrencyThousandSeparator = (value) => value === importerModulePublication_1.CurrencyThousandSeparatorKind.dot ? exports.SEPARATOR_DOT :
    value === importerModulePublication_1.CurrencyThousandSeparatorKind.comma ? exports.SEPARATOR_COMA :
        value === importerModulePublication_1.CurrencyThousandSeparatorKind.space ? exports.NBSP :
            undefined;
FormatService.resolvePrefixSufix = (placement, unit) => ({
    prefix: placement === importerModulePublication_1.UnitSymbolPlacementKind.beforeValue ? unit.value + exports.NBSP : undefined,
    suffix: placement === importerModulePublication_1.UnitSymbolPlacementKind.afterValue ? exports.NBSP + unit.value : undefined
});
FormatService.resolveCurrencyPrefixSufix = (placement, currency) => ({
    prefix: currency !== exports.NO_CURRENCY_KEY && placement === importerModulePublication_1.CurrencySymbolPlacementKind.beforePrice
        ? (currency + exports.NBSP) : undefined,
    suffix: currency !== exports.NO_CURRENCY_KEY && placement === importerModulePublication_1.CurrencySymbolPlacementKind.afterPrice
        ? (exports.NBSP + currency) : undefined
});
